import React, { Fragment, useContext } from 'react';
import styles from './styles.scss';
import colorVariables from '@densityco/ui/variables/colors.json';

import { CaptureViewerVideoContext } from '.';

const DRAG_HANDLE_RADIUS_IN_PX = 4;

const DragHandle: React.FunctionComponent<{
  x: number;
  y: number;
  onChange: (x: number, y: number) => void;
  onMouseUp?: (x: number, y: number) => void;
  testId?: string;
}> = ({ x, y, onChange, onMouseUp = () => null, testId = null }) => {
  const videoRef = useContext(CaptureViewerVideoContext);
  return (
    <circle
      id="draghandle"
      data-test-id={`capture-viewer-drag-handle${testId ? `-${testId}` : ''}`}
      cx={x}
      cy={y}
      r={DRAG_HANDLE_RADIUS_IN_PX}
      fill={colorVariables.gray300}
      stroke="black"
      strokeWidth={1}
      style={{ cursor: 'move' }}
      onMouseDown={e => {
        // Get upper left coordinates of video, so that we know where it is on the page.
        const videoBbox = videoRef.getBoundingClientRect();
        const [videoOffsetX, videoOffsetY] = [videoBbox.left, videoBbox.top];
        const videoWidth = videoBbox.width,
          videoHeight = videoBbox.height;

        function convertToSvgCoordinates(x, y) {
          return [(x / videoWidth) * 160, (y / videoHeight) * 120];
        }
        function convertToPixelCoordinates(x, y) {
          return [(x / 160) * videoWidth, (y / 120) * videoHeight];
        }

        // Convert the x and y coordinates that were used to position the circle from the svg
        // coordinate space (ie, 160x120 svg viewbox) into the page pixel coordinate space.
        const [initialX, initialY] = convertToPixelCoordinates(x, y);

        // Determine the difference in position between the mouse position and the
        // center of the circle - this "fixed offset" will be added to all further
        // mouse movements so that the point that the drag handle is moved from is
        // whereever the user initially clicked.
        let offsetX = initialX - (e.clientX - videoOffsetX),
          offsetY = initialY - (e.clientY - videoOffsetY);

        const update = e => {
          const clientX = e.clientX,
            clientY = e.clientY;

          // Determine the absolute coordinates the mouse is at
          // from the top left of the video.
          const videoX = clientX - videoOffsetX,
            videoY = clientY - videoOffsetY;

          // Add initial offsets that were determined on initial mouse down
          let x = videoX + offsetX,
            y = videoY + offsetY;

          // Bounds checking
          if (x < 0) {
            x = 0;
          }
          if (x > videoWidth) {
            x = videoWidth;
          }
          if (y < 0) {
            y = 0;
          }
          if (y > videoHeight) {
            y = videoHeight;
          }

          // Finally, convert these x and y coordinates from the video coordinate system to be the svg
          // coordinate system - the 160x120 viewbox.
          const [svgX, svgY] = convertToSvgCoordinates(x, y);
          onChange(svgX, svgY);
          return [svgX, svgY];
        };
        window.addEventListener('mousemove', update);

        window.addEventListener(
          'mouseup',
          e => {
            window.removeEventListener('mousemove', update);
            const [svgX, svgY] = update(e);
            onMouseUp(svgX, svgY);
          },
          { once: true },
        );
      }}
    />
  );
};

export default DragHandle;
